import { CartHeaderState, INITIAL_CART_HEADER_CONTEXT_VALUE } from "@cart-client/contexts/CartHeaderContext";
import { isValuesEqual, updateCartHeaderState } from "@cart-client/helpers/cartHeaderLottie";
import { IBenefitReinforcementComms } from "@swiggy-private/cart-types";
import { isEmpty } from "@swiggy-private/js-utils";
import { IApplyFrequencyCappingLogic, IUpdateStateForLottieDisplay } from "./types";

// helpers
export const isStateEqual = (newCartHeaderState: CartHeaderState, cartHeaderState: CartHeaderState) => {
    return (
        JSON.stringify(newCartHeaderState) === JSON.stringify(cartHeaderState) ||
        isValuesEqual(newCartHeaderState, cartHeaderState)
    );
};

export const hideTheme = () => {
    return { ...INITIAL_CART_HEADER_CONTEXT_VALUE.cartHeaderState, hideTheme: true };
};

interface StartAnimationProps {
    newCartHeaderState: CartHeaderState;
    setCartHeaderState: React.Dispatch<React.SetStateAction<CartHeaderState>>;
    frequencyData?: IBenefitReinforcementComms;
    timeoutRef: React.MutableRefObject<NodeJS.Timeout | undefined>;
}

export const startAnimation = ({
    newCartHeaderState,
    setCartHeaderState,
    frequencyData,
    timeoutRef,
}: StartAnimationProps) => {
    if (
        !(
            !newCartHeaderState.hideTheme &&
            !newCartHeaderState.showFinalState &&
            (frequencyData?.delay?.value || newCartHeaderState.isBolt) &&
            !newCartHeaderState.startAnimation
        )
    ) {
        return;
    }
    if (newCartHeaderState.isBolt) {
        setCartHeaderState(prev => {
            if (!prev.hideTheme) {
                return { ...prev, startAnimation: true };
            }

            return prev;
        });
    } else if (frequencyData) {
        // for delay start and non bolt
        const timeoutId = setTimeout(() => {
            setCartHeaderState(prev => {
                if (!prev.hideTheme) {
                    return { ...prev, startAnimation: true };
                }
                return prev;
            });
        }, frequencyData.delay.value * 1000);
        if (timeoutRef) {
            timeoutRef.current = timeoutId;
        }
    }
    return;
};

export const applyFrequencyCappingLogic = ({
    newCartHeaderState,
    isThemeHidden,
    cartHeaderSessionObject,
    sessionFreqCapKey,
    sessionCount,
    rxId,
    sid,
    INITIAL_CART_HEADER_CONTEXT_VALUE,
    cartLocalStorageKey,
    frequencyData,
    sendAnalytics,
    isBolt,
    boltUpdateKey,
}: IApplyFrequencyCappingLogic) => {
    // Early return for simple cases
    if (isBolt) {
        if (isThemeHidden) {
            const hiddenState = hideTheme();
            return { isThemeHidden: true, updatedState: hiddenState };
        }
        const { newCartHeaderState: updatedState, shouldHideTheme } = updateCartHeaderState({
            rxId,
            INITIAL_CART_HEADER_CONTEXT_VALUE,
            sendAnalytics,
            newCartHeaderState,
            isBolt,
            cartHeaderSessionObject,
            sessionFreqCapKey,
            sid,
            cartLocalStorageKey,
            boltUpdateKey,
        });

        return { isThemeHidden: shouldHideTheme, updatedState };
    } else {
        if (isThemeHidden || isEmpty(frequencyData)) {
            const hiddenState = hideTheme();
            return { isThemeHidden: true, updatedState: hiddenState };
        }
        const { frequency_cap: frequencyCap } = frequencyData;
        const { id: frequencyCapId, count } = frequencyCap;

        // Frequency Capping logic
        const { newCartHeaderState: updatedState, shouldHideTheme } = updateCartHeaderState({
            cartHeaderSessionObject,
            frequencyCapId,
            count,
            rxId,
            sessionFreqCapKey,
            sessionCount,
            sid,
            INITIAL_CART_HEADER_CONTEXT_VALUE,
            cartLocalStorageKey,
            sendAnalytics,
            newCartHeaderState,
            isBolt,
        });
        return { isThemeHidden: shouldHideTheme, updatedState };
    }
};

export const updateStateForLottieDisplay = ({
    newCartHeaderState,
    isThemeHidden,
    isBlck,
    isOtg,
    isExpress,
    isSuccess,
    isBolt,
}: IUpdateStateForLottieDisplay): CartHeaderState => {
    if (isThemeHidden) {
        return { ...newCartHeaderState, isStatusSuccess: isSuccess };
    }

    // Create object in one go instead of multiple spreads
    return {
        ...newCartHeaderState,
        isStatusSuccess: isSuccess,
        isBlack: isBlck,
        isOtg,
        isExpress,
        isBolt,
        showAnimation: true,
        hideTheme: false,
    };
};
